/**
 * Admin — KYC submission review page.
 *
 * Lists KycSubmission rows from /api/admin/kyc and lets admins
 * approve or reject pending submissions.
 */
import { useState } from 'react';
import {
  Button, Modal, Input, Tag, Select, Space, Descriptions, message,
} from 'antd';
import { DataTable } from '../../components/DataTable';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { useQueryClient } from '@tanstack/react-query';

const STATUS_COLORS: Record<string, string> = {
  pending: 'orange',
  approved: 'green',
  rejected: 'red',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待审核',
  approved: '已通过',
  rejected: '已拒绝',
};

const ID_TYPE_LABELS: Record<string, string> = {
  id_card: '身份证',
  passport: '护照',
  driver_license: '驾照',
};

export function KycSubmissionsPage() {
  const hasNode = useAuthStore((s) => s.hasNode);
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>(undefined);

  // Review modal
  const [reviewVisible, setReviewVisible] = useState(false);
  const [reviewRow, setReviewRow] = useState<any>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [rejectReason, setRejectReason] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Detail modal
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRow, setDetailRow] = useState<any>(null);

  const canReview = hasNode('kyc.review');

  const handleReview = async () => {
    if (!reviewRow) return;
    if (reviewAction === 'reject' && !rejectReason.trim()) {
      message.warning('请输入拒绝原因');
      return;
    }
    setReviewLoading(true);
    try {
      const body: any = { action: reviewAction };
      if (reviewAction === 'reject') body.reject_reason = rejectReason;
      const res: any = await api.post(`/api/admin/kyc/${reviewRow.id}/review`, body);
      message.success(reviewAction === 'approve' ? '已通过' : '已拒绝');
      setReviewVisible(false);
      setReviewRow(null);
      setRejectReason('');
      qc.invalidateQueries({ queryKey: ['kyc-submissions'] });
    } catch (e: any) {
      message.error(e?.response?.data?.msg || e.message || '操作失败');
    } finally {
      setReviewLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 80 },
    { title: '真实姓名', dataIndex: 'real_name', key: 'real_name', width: 120 },
    {
      title: '证件类型', dataIndex: 'id_type', key: 'id_type', width: 90,
      render: (v: string) => ID_TYPE_LABELS[v] || v,
    },
    { title: '证件号码', dataIndex: 'id_number', key: 'id_number', width: 160 },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{STATUS_LABELS[v] || v}</Tag>,
    },
    { title: '拒绝原因', dataIndex: 'reject_reason', key: 'reject_reason', width: 160 },
    {
      title: '提交时间', dataIndex: 'create_time', key: 'create_time', width: 170,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作', key: 'action', width: 180, fixed: 'right' as const,
      render: (_: any, row: any) => (
        <Space size="small">
          <Button size="small" onClick={() => { setDetailRow(row); setDetailVisible(true); }}>
            详情
          </Button>
          {canReview && row.status === 'pending' && (
            <>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  setReviewRow(row);
                  setReviewAction('approve');
                  setReviewVisible(true);
                }}
              >
                通过
              </Button>
              <Button
                size="small"
                danger
                onClick={() => {
                  setReviewRow(row);
                  setReviewAction('reject');
                  setRejectReason('');
                  setReviewVisible(true);
                }}
              >
                拒绝
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 160 }}
          placeholder="筛选状态"
          allowClear
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
          options={[
            { value: 'pending', label: '待审核' },
            { value: 'approved', label: '已通过' },
            { value: 'rejected', label: '已拒绝' },
          ]}
        />
        <Input
          style={{ width: 120 }}
          placeholder="用户ID"
          type="number"
          value={userIdFilter ?? ''}
          onChange={(e) => {
            const v = e.target.value ? Number(e.target.value) : undefined;
            setUserIdFilter(v);
          }}
          allowClear
        />
      </Space>

      <DataTable<any>
        queryKey={['kyc-submissions']}
        endpoint="/api/admin/kyc"
        extraParams={{
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(userIdFilter ? { user_id: userIdFilter } : {}),
        }}
        columns={columns}
        rowKey="id"
      />

      {/* Detail modal */}
      <Modal
        title="KYC 详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {detailRow && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{detailRow.id}</Descriptions.Item>
            <Descriptions.Item label="用户ID">{detailRow.user_id}</Descriptions.Item>
            <Descriptions.Item label="真实姓名">{detailRow.real_name}</Descriptions.Item>
            <Descriptions.Item label="证件类型">{ID_TYPE_LABELS[detailRow.id_type] || detailRow.id_type}</Descriptions.Item>
            <Descriptions.Item label="证件号码">{detailRow.id_number}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_COLORS[detailRow.status]}>{STATUS_LABELS[detailRow.status] || detailRow.status}</Tag>
            </Descriptions.Item>
            {detailRow.reject_reason && (
              <Descriptions.Item label="拒绝原因">{detailRow.reject_reason}</Descriptions.Item>
            )}
            {detailRow.front_image_url && (
              <Descriptions.Item label="证件正面">{detailRow.front_image_url}</Descriptions.Item>
            )}
            {detailRow.back_image_url && (
              <Descriptions.Item label="证件反面">{detailRow.back_image_url}</Descriptions.Item>
            )}
            {detailRow.selfie_url && (
              <Descriptions.Item label="自拍照">{detailRow.selfie_url}</Descriptions.Item>
            )}
            <Descriptions.Item label="审核人ID">{detailRow.reviewed_by ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="审核时间">{detailRow.reviewed_at ? new Date(detailRow.reviewed_at).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
            <Descriptions.Item label="提交时间">{new Date(detailRow.create_time).toLocaleString('zh-CN')}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Review modal */}
      <Modal
        title={reviewAction === 'approve' ? '通过 KYC' : '拒绝 KYC'}
        open={reviewVisible}
        onCancel={() => { setReviewVisible(false); setReviewRow(null); }}
        onOk={handleReview}
        confirmLoading={reviewLoading}
        okText={reviewAction === 'approve' ? '确认通过' : '确认拒绝'}
        okButtonProps={reviewAction === 'reject' ? { danger: true } : {}}
      >
        {reviewRow && (
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="用户ID">{reviewRow.user_id}</Descriptions.Item>
            <Descriptions.Item label="姓名">{reviewRow.real_name}</Descriptions.Item>
            <Descriptions.Item label="证件号">{reviewRow.id_number}</Descriptions.Item>
          </Descriptions>
        )}
        {reviewAction === 'reject' && (
          <Input.TextArea
            rows={3}
            placeholder="请输入拒绝原因（必填）"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        )}
      </Modal>
    </div>
  );
}

export default KycSubmissionsPage;
